import { db } from '../firebase';
import {
  collection, doc, query, where, getDocs,
  writeBatch, serverTimestamp
} from 'firebase/firestore';
import { normaliseStatus } from '../constants/arcStatus';

/**
 * Update ARC request status in BOTH locations atomically:
 * 1. Top-level arcRequests/{requestId}
 * 2. authors/{authorId}/campaigns/{campaignId}/applications/{applicationId}
 *
 * Finds the matching documents by readerId + bookId when IDs aren't known.
 *
 * @param {Object} params
 * @param {string} params.authorId       - The author's UID
 * @param {string} params.campaignId     - Campaign ID (optional if not known)
 * @param {string} params.applicationId  - Application doc ID (optional if not known)
 * @param {string} params.readerId       - Reader's UID
 * @param {string} params.bookId         - Book ID (used to match arcRequests)
 * @param {string} params.newStatus      - New status value
 * @param {string} [params.requestId]    - Top-level arcRequest doc ID (if known)
 */
export async function updateArcStatus({
  authorId,
  campaignId,
  applicationId,
  readerId,
  bookId,
  newStatus,
  requestId,
}) {
  const status = normaliseStatus(newStatus);
  const batch = writeBatch(db);
  const now = serverTimestamp();

  // ── 1. Update campaign application ──────────────────────
  if (authorId && campaignId && applicationId) {
    const appRef = doc(
      db, 'authors', authorId, 'campaigns', campaignId, 'applications', applicationId
    );
    const updates = { status };
    if (status === 'approved') updates.approvedAt = now;
    if (status === 'declined') updates.declinedAt = now;
    batch.update(appRef, updates);
  } else if (authorId && readerId) {
    // Find application docs across all campaigns for this author + reader
    const campaignsSnap = await getDocs(collection(db, 'authors', authorId, 'campaigns'));
    for (const campaignDoc of campaignsSnap.docs) {
      const appsQ = query(
        collection(db, 'authors', authorId, 'campaigns', campaignDoc.id, 'applications'),
        where('readerId', '==', readerId)
      );
      const appsSnap = await getDocs(appsQ);
      for (const appDoc of appsSnap.docs) {
        const updates = { status };
        if (status === 'approved') updates.approvedAt = now;
        if (status === 'declined') updates.declinedAt = now;
        batch.update(appDoc.ref, updates);
      }
    }
  }

  // ── 2. Update top-level arcRequests ─────────────────────
  if (requestId) {
    // Direct ID known
    const arcRef = doc(db, 'arcRequests', requestId);
    const updates = { status };
    if (status === 'approved') updates.approvedAt = now;
    if (status === 'declined') updates.declinedAt = now;
    batch.update(arcRef, updates);
  } else if (readerId) {
    // Find by readerId (and optionally bookId)
    let arcQ;
    if (bookId) {
      arcQ = query(
        collection(db, 'arcRequests'),
        where('readerId', '==', readerId),
        where('bookId', '==', bookId)
      );
    } else {
      arcQ = query(
        collection(db, 'arcRequests'),
        where('readerId', '==', readerId)
      );
    }
    const arcSnap = await getDocs(arcQ);
    for (const arcDoc of arcSnap.docs) {
      const updates = { status };
      if (status === 'approved') updates.approvedAt = now;
      if (status === 'declined') updates.declinedAt = now;
      batch.update(arcDoc.ref, updates);
    }
  }

  // ── 3. Also sync to author-scoped arcRequests ───────────
  if (authorId && readerId) {
    let authorArcQ;
    if (bookId) {
      authorArcQ = query(
        collection(db, 'authors', authorId, 'arcRequests'),
        where('readerId', '==', readerId),
        where('bookId', '==', bookId)
      );
    } else {
      authorArcQ = query(
        collection(db, 'authors', authorId, 'arcRequests'),
        where('readerId', '==', readerId)
      );
    }
    try {
      const authorArcSnap = await getDocs(authorArcQ);
      for (const arcDoc of authorArcSnap.docs) {
        const updates = { status };
        if (status === 'approved') updates.approvedAt = now;
        if (status === 'declined') updates.declinedAt = now;
        batch.update(arcDoc.ref, updates);
      }
    } catch {
      // Author-scoped arcRequests may not exist — non-critical
    }
  }

  await batch.commit();
  return status;
}
